// Administration route, in the one encoding that can be sent.
//
// `RXR.1` and the `X0162-0` reimbursement claim carry HL7 table 0162 codes, and
// `HealthlinkTable0162.getLabelByValue` returns null for anything it does not
// recognise — so a display label stored here goes out as an invalid route with a null
// CE.2 rather than failing loudly (KIN-1680).
//
// Shared deliberately. These values were duplicated in the vaccine form and in the
// settings catalogue, and the two copies disagreed: the form had moved to codes in
// February 2026 while settings kept offering the old labels, so the catalogue was still
// creating the encoding the form had stopped producing. One list cannot drift from
// itself.

export const ADMINISTRATION_METHOD_OPTIONS = [
  { label: "Intramuscular", value: "IM" },
  { label: "Intranasal", value: "IN" },
  { label: "Oral", value: "PO" },
  { label: "Subcutaneous", value: "SC" },
  { label: "Intravenous", value: "IV" },
  { label: "Intradermal", value: "ID" },
];

// The labels offered before the February 2026 switch, mapped to their codes.
//
// All six the old list held, not just the four that happen to appear in one tenant's
// rows: a record carrying any of them otherwise heals to nothing and re-emits the
// label. Two were wrong twice over — 0162 has no `SQ`, and `Intranasal` never carried a
// code. The set comes from the list as it stood before the switch, so it is closed.
export const LEGACY_ADMINISTRATION_METHOD_CODES = {
  "Intramuscular (IM)": "IM",
  "Subcutaneous (SQ)": "SC",
  "Oral (PO)": "PO",
  Intranasal: "IN",
  "Intravenous (IV)": "IV",
  "Intradermal (ID)": "ID",
};

// The catalogue states a product's route as a plain display label — `VaccineCodeDTO`'s
// `routeOfAdministration` is "Intramuscular", not "IM" (KIN-1679). Same vocabulary as the
// options above, so it is derived from them rather than written out a second time.
const CODE_BY_LABEL = Object.fromEntries(
  ADMINISTRATION_METHOD_OPTIONS.map(({ label, value }) => [label, value])
);

/**
 * The code for a value, whichever of the three encodings it arrives in: the code itself, a
 * legacy stored label ("Subcutaneous (SQ)"), or the catalogue's plain label ("Oral").
 *
 * Returns an empty string for a value in none of them. It never guesses and never falls
 * back to a default: a route nobody stated is not a route, and recording a dose as
 * intramuscular because nothing else matched is how an oral vaccine ends up on the chart
 * as an injection (KIN-2115).
 */
export const toAdministrationMethodCode = (storedValue) => {
  if (!storedValue) return "";

  const legacyCode = LEGACY_ADMINISTRATION_METHOD_CODES[storedValue];
  if (legacyCode) return legacyCode;

  const isAlreadyACode = ADMINISTRATION_METHOD_OPTIONS.some(
    (option) => option.value === storedValue
  );
  if (isAlreadyACode) return storedValue;

  return CODE_BY_LABEL[storedValue] ?? "";
};
